import React from "react";

import {
  AnnualIncreaseLabels,
  AnnualIncreaseType,
  AnnualIncreaseTypeLabel,
  AnnualIncreaseTypes,
} from "@fartherfinance/frontend/api/FinancialPlans/types";

import MultiDropdownInput from "../MultiDropdownInput";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import {
  formNumberFieldRules,
  formPercentFieldCustomRegex,
} from "@src/utils/formValidation";

interface NoInflationObjType {
  type: typeof AnnualIncreaseTypes[0];
}
interface GeneralInflationObjType {
  type: typeof AnnualIncreaseTypes[1];
}

export const CustomInflation = AnnualIncreaseTypes[2];
interface CustomInflationObjType {
  type: typeof CustomInflation;
}

export const isCustomInflation = (
  obj:
    | NoInflationObjType
    | GeneralInflationObjType
    | CustomInflationObjType
    | undefined
): obj is CustomInflationObjType => {
  return obj?.type === AnnualIncreaseTypes[2];
};

// IMPORTANT! Must import these field names into the form that uses this component
export const AnnualIncreaseTypeField = "AnnualIncreaseTypeField";
export const AnnualIncreaseAmountField = "AnnualIncreaseAmountField";

interface AnnualIncreaseTypeOption {
  label: AnnualIncreaseTypeLabel;
  value: AnnualIncreaseType;
}

export type AnnualIncreaseFields = {
  [AnnualIncreaseTypeField]: {
    label: AnnualIncreaseTypeLabel;
    value: AnnualIncreaseType;
  };
  [AnnualIncreaseAmountField]: string | undefined;
};

export const annualIncreaseTypeOptions: AnnualIncreaseTypeOption[] =
  AnnualIncreaseTypes.map((tf, i) => ({
    label: AnnualIncreaseLabels[i],
    value: tf,
  }));

const CustomInflationOption = annualIncreaseTypeOptions.filter(
  (el) => el.label === "Custom Inflation"
);

export const defaultAnnualIncreaseTypeField = {
  label: "None" as AnnualIncreaseTypeLabel,
  value: "None" as AnnualIncreaseType,
};

export const defaultAnnualIncreaseFieldValues = {
  AnnualIncreaseTypeField: { ...defaultAnnualIncreaseTypeField },
  AnnualIncreaseAmountField: undefined,
};

interface Props {
  fieldLabel: string;
  additionalInputFieldLabel: string;
  curOption: string;
  isDisabled?: boolean;
}

const AnnualIncreaseMultiDropdownInput: React.FC<Props> = ({
  fieldLabel = "Annual Increase Type",
  additionalInputFieldLabel = "Custom Annual Inflation",
  curOption,
  isDisabled,
}) => {
  return (
    <MultiDropdownInput
      fieldName={AnnualIncreaseTypeField}
      fieldLabel={fieldLabel}
      curOption={curOption}
      options={annualIncreaseTypeOptions}
      isDisabled={isDisabled}
      displayInput1OnOptions={CustomInflationOption}
      additionalInput1={
        <FormNumberField
          name={AnnualIncreaseAmountField}
          label={additionalInputFieldLabel}
          endAdornment={"%"}
          customRegex={formPercentFieldCustomRegex}
          rules={formNumberFieldRules({
            required: true,
            minValue: 0.01,
            maxValue: 100,
          })}
        />
      }
      fillUpSpaceIfNotRenderedInput1={false}
    />
  );
};

export default AnnualIncreaseMultiDropdownInput;
